import { useState } from "react";
import { Play, Send, Check, Undo2, History, CalendarDays } from "lucide-react";
import { Button } from "@/components/ui/button";
import { StatusBadge } from "@/components/crm/common";
import { FileBox } from "@/components/crm/Files";
import { PersonChip } from "@/components/crm/UserImage";

const today = () => new Date().toISOString().slice(0, 10);

function Progress({ value, testid }) {
  return (
    <div className="flex items-center gap-2">
      <div className="h-2 flex-1 overflow-hidden rounded-full bg-zinc-100"><div className="h-full rounded-full bg-[#A9792C] transition-[width] duration-500" style={{ width: `${value || 0}%` }} /></div>
      <span data-testid={testid} className="w-9 text-right font-mono text-xs text-zinc-600">{value || 0}%</span>
    </div>
  );
}

function ArtistActions({ t, act, id }) {
  const [p, setP] = useState(t.progress || 10);
  const [note, setNote] = useState("");
  const working = ["In Progress", "Changes Requested"].includes(t.status);
  return (
    <div className="space-y-3 border-t border-zinc-100 pt-3">
      {["Pending", "Changes Requested", "Delayed"].includes(t.status) && <Button data-testid={`${id}-start`} size="sm" onClick={() => act("start")}><Play className="mr-1 h-3.5 w-3.5" />{t.status === "Changes Requested" ? "Start revisions" : "Start work"}</Button>}
      {working && (
        <div className="flex flex-wrap items-center gap-2">
          <input data-testid={`${id}-progress-slider`} type="range" min="0" max="95" step="5" value={p} onChange={(e) => setP(Number(e.target.value))} className="w-36 accent-[#A9792C]" />
          <span className="font-mono text-xs">{p}%</span>
          <input data-testid={`${id}-progress-note`} value={note} onChange={(e) => setNote(e.target.value)} placeholder="What did you finish?" className="h-8 min-w-[140px] flex-1 rounded-md border border-zinc-200 px-2 text-xs outline-none focus:border-[#A9792C]" />
          <Button data-testid={`${id}-progress-save`} size="sm" variant="outline" onClick={async () => { await act("progress", { progress: p, note }); setNote(""); }}>Update</Button>
        </div>
      )}
      {t.status !== "Complete" && t.status !== "HOD Review" && (
        <>
          <FileBox compact testid={`${id}-files`} entityType="task" entityId={t.id} title="Final files / renders" canUpload />
          <Button data-testid={`${id}-submit`} size="sm" className="bg-zinc-900" onClick={() => act("submit", { note })}><Send className="mr-1 h-3.5 w-3.5" />Finished — send to HOD</Button>
        </>
      )}
    </div>
  );
}

function HodActions({ t, act, id, onChanges }) {
  return (
    <div className="space-y-3 border-t border-zinc-100 pt-3">
      <FileBox compact testid={`${id}-review-files`} entityType="task" entityId={t.id} title="Submitted files" canUpload={false} />
      <div className="flex flex-wrap gap-2">
        <Button data-testid={`${id}-approve`} size="sm" className="bg-emerald-600 hover:bg-emerald-700" onClick={() => act("review", { decision: "approve", comment: "" })}><Check className="mr-1 h-3.5 w-3.5" />Approve</Button>
        <Button data-testid={`${id}-changes`} size="sm" variant="outline" onClick={onChanges}><Undo2 className="mr-1 h-3.5 w-3.5" />Request changes</Button>
      </div>
    </div>
  );
}

export function TaskCard({ t, mode, act, onChanges }) {
  const [hist, setHist] = useState(false);
  const id = `artist-task-${t.id}`;
  const late = t.deadline && t.deadline < today() && t.status !== "Complete";
  return (
    <div data-testid={id} className="space-y-3 rounded-xl border border-zinc-200 bg-white p-4 shadow-[0_1px_2px_rgba(16,24,40,0.04)] transition-shadow hover:shadow-md">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0"><div className="font-semibold text-zinc-900">{t.deliverable}</div><div className="truncate text-xs text-zinc-500">{t.project_name} · {t.department}</div></div>
        <StatusBadge testid={`${id}-status`} value={t.status} />
      </div>
      <Progress value={t.progress ?? (t.status === "Complete" ? 100 : 0)} testid={`${id}-progress`} />
      <dl className="grid grid-cols-2 gap-x-3 gap-y-1 text-xs">
        <div><dt className="text-zinc-400">Artist</dt><dd className="font-medium text-zinc-700"><PersonChip name={t.assignee} size={18} /></dd></div>
        <div><dt className="text-zinc-400">Lead</dt><dd className="text-zinc-700">{t.team_lead || "—"}</dd></div>
        <div><dt className="text-zinc-400">HOD</dt><dd className="text-zinc-700">{t.hod || "—"}</dd></div>
        <div><dt className="text-zinc-400">Deadline</dt><dd className={`flex items-center gap-1 ${late ? "font-semibold text-rose-600" : "text-zinc-700"}`}><CalendarDays className="h-3 w-3" />{t.deadline}{late && " · overdue"}{late && t.overdue_alerted_for === t.deadline && <span data-testid={`${id}-overdue-alerted`} className="ml-1 font-normal text-zinc-400">(reminded)</span>}</dd></div>
      </dl>
      {t.priority && t.priority !== "Normal" && <StatusBadge value={t.priority} />}
      {t.reference && <div className="rounded-md bg-zinc-50 p-2 text-xs text-zinc-600">{t.reference}</div>}
      {t.status === "Changes Requested" && t.hod_comment && <div data-testid={`${id}-hod-comment`} className="rounded-md border-l-4 border-amber-400 bg-amber-50 p-2 text-xs text-amber-900"><b>HOD changes:</b> {t.hod_comment}</div>}
      {t.next_action && <div className="text-xs text-zinc-500"><span className="text-zinc-400">Next:</span> {t.next_action}</div>}
      {mode === "artist" && <ArtistActions t={t} act={(a, b) => act(t, a, b)} id={id} />}
      {mode === "hod" && t.status === "HOD Review" && <HodActions t={t} act={(a, b) => act(t, a, b)} id={id} onChanges={() => onChanges(t)} />}
      {(t.history || []).length > 0 && (
        <div>
          <button data-testid={`${id}-history-toggle`} onClick={() => setHist((h) => !h)} className="flex items-center gap-1 text-[11px] font-medium text-zinc-500 hover:text-zinc-800"><History className="h-3 w-3" />{hist ? "Hide" : "Show"} timeline ({t.history.length})</button>
          {hist && (
            <ol className="mt-2 space-y-1.5 border-l border-zinc-200 pl-3">
              {[...t.history].reverse().map((h, i) => <li key={i} className="text-[11px] text-zinc-600"><b className="text-zinc-800">{h.action}</b> · {h.by} · {new Date(h.at).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" })}{h.note && <div className="text-zinc-500">{h.note}</div>}</li>)}
            </ol>
          )}
        </div>
      )}
    </div>
  );
}
